import type { OutcomeDefinition } from "@repo/app-types/entities/outcome";
import { defaultOutcomeDefinitions } from "@repo/app-types/entities/outcome";
import {
  collectPlaybookCriteria,
  type PlaybookTree,
} from "@repo/app-types/entities/playbook";
import type { ChatSuggestion } from "@repo/ui/chat/chatEmptyState";

// Each configuration prompt ends by asking the agent to put its change up as a
// playbook proposal, so the run finishes with a reviewable card the user can
// apply in the editor rather than a wall of prose.
const PROPOSE_HINT =
  "When you land on a concrete change, create a playbook proposal I can review and apply.";

const DRAFT_OUTCOME_PROMPT = `Analyze customer interactions and draft an outcome for this playbook.

Recommend the best outcome template from the available set of outcome templates for these customer interactions.

Then draft the outcome context that should be used to assign the outcome labels for that template. The context should clearly explain when each label applies and how to handle ambiguous cases so an AI grader can assign the outcome consistently.

The outcome should help us understand whether these interactions are driving the business result we care about.

${PROPOSE_HINT}`;

const IDENTIFY_KB_GAPS_PROMPT = `Analyze the recent customer interactions and identify knowledge base content we should add.

Look for repeated customer questions, moments where agents seem uncertain, policy or process details that are unclear, and information gaps that force agents to improvise.

Respond with a bulleted list of specific knowledge base content categories we should add or improve and the interaction patterns that support it.`;

const DRAFT_PLAYBOOK_PROMPT = `Analyze customer interactions to build a high quality playbook. Look for the substantive parts of the customer journey that should be captured in the playbook, and the agent behaviors we want to track and reinforce.

Start by thinking through the core sections that we should have, and then list the criteria for each. Each section you recommend needs a Name and Description that clearly explains to the AI grader when the section is applicable or not applicable (and should not be graded).

For each section, also include a bullet point list of criteria names - include only the most important criteria. Avoid criteria that are duplicative of each other, don't add meaningful new signal, or are overly opinionated about the way a call should progress that isn't actually correlated with the best customer experience.

A good playbook should allow us to understand the quality of the customer experience each agent is providing, and give us something to optimize toward to improve the customer experience.

${PROPOSE_HINT}`;

const REVIEW_PLAYBOOK_IMPROVEMENTS_PROMPT = `Review this playbook for any potential improvements that can help us track and drive a better customer experience.

Audit the playbook with a focus on:
- whether each section is actually relevant and distinct
- whether section applicability is clear enough for the AI grader to know when a section should or should not be graded
- whether any criteria are vague, duplicative, too opinionated, too low-signal, missing important grading context, or don't actually represent something that materially impacts the customer experience.

Use the current criteria/section details, applicability patterns, recent interaction evidence, and proposal validation to guide your recommendations.
If you have an idea for an improvement, be sure to test its performance on select customer interactions to ensure that it actually improves results.

${PROPOSE_HINT}`;

/** The playbook still carries an untouched default outcome template. */
function hasDefaultOutcomeDefinitionText(
  outcomeDefinition: OutcomeDefinition | null | undefined,
): boolean {
  if (!outcomeDefinition) {
    return false;
  }
  const defaultOutcomeDefinition = defaultOutcomeDefinitions.find(
    ({ id }) => id === outcomeDefinition.id,
  );
  if (!defaultOutcomeDefinition) {
    return false;
  }
  return (
    JSON.stringify(outcomeDefinition) ===
    JSON.stringify(defaultOutcomeDefinition)
  );
}

/**
 * The playbook configuration starters, chosen from the playbook's current
 * state: draft an outcome when there isn't a meaningful one, draft the whole
 * playbook when it's still sparse or build on it once it has criteria, and
 * surface knowledge-base gaps where that's enabled. A not-yet-loaded playbook
 * (a brand-new one, or one still fetching) reads as empty, which is exactly the
 * "draft it" case. Replaces the deleted playbook configuration agent's pills.
 */
export function getPlaybookConfigSuggestions(args: {
  playbookTree: PlaybookTree | undefined;
  outcomeDefinition: OutcomeDefinition | null | undefined;
  isKnowledgeBaseEnabled: boolean;
}): ChatSuggestion[] {
  const suggestions: ChatSuggestion[] = [];

  if (
    !args.outcomeDefinition ||
    hasDefaultOutcomeDefinitionText(args.outcomeDefinition)
  ) {
    suggestions.push({
      label: "Draft an Outcome for this playbook",
      prompt: DRAFT_OUTCOME_PROMPT,
    });
  }

  if (args.isKnowledgeBaseEnabled) {
    suggestions.push({
      label:
        "Identify knowledge base content we should add based on recent customer interactions",
      prompt: IDENTIFY_KB_GAPS_PROMPT,
    });
  }

  const criteriaCount = args.playbookTree
    ? collectPlaybookCriteria(args.playbookTree).length
    : 0;

  if (criteriaCount < 5) {
    suggestions.push({
      label: "Draft this playbook based on recent customer interactions",
      prompt: DRAFT_PLAYBOOK_PROMPT,
    });
  } else {
    suggestions.push({
      label: "Review this playbook for any potential improvements",
      prompt: REVIEW_PLAYBOOK_IMPROVEMENTS_PROMPT,
    });
  }

  return suggestions;
}
